import { z } from 'zod';

/**
 * Definition for an Office.js tool that can be called by the AI backend.
 */
export interface OfficeToolDefinition {
  name: string;
  description: string;
  parameters: z.ZodSchema;
  handler: (params: any, context?: unknown) => Promise<unknown> | unknown;
  /** When true, the user must approve the tool call before it executes. */
  requiresApproval?: boolean;
}

export const getDocumentInfoTool: OfficeToolDefinition = {
  name: 'get_document_info',
  description:
    'Get information about the current Office document, including the host application name and document title or filename.',
  parameters: z.object({}),
  handler: async () => {
    const host = Office.context.host;
    let hostName = 'Unknown';

    switch (host) {
      case Office.HostType.Word:
        hostName = 'Word';
        break;
      case Office.HostType.Excel:
        hostName = 'Excel';
        break;
      case Office.HostType.PowerPoint:
        hostName = 'PowerPoint';
        break;
      case Office.HostType.Outlook:
        hostName = 'Outlook';
        break;
    }

    let documentTitle = 'Untitled';
    try {
      if (Office.context.document) {
        const url = Office.context.document.url;
        if (url) {
          documentTitle = url.split('/').pop() || url;
        }
      }
    } catch {
      // Document URL may not be available
    }

    return {
      hostApplication: hostName,
      documentTitle,
      platform: Office.context.platform?.toString() || 'Unknown',
    };
  },
};

export const getSelectionTool: OfficeToolDefinition = {
  name: 'get_selection',
  description:
    'Get the currently selected text in the Office document. Works across Word, Excel, and PowerPoint.',
  parameters: z.object({}),
  handler: async () => {
    return new Promise<{ selectedText: string }>((resolve, reject) => {
      Office.context.document.getSelectedDataAsync(
        Office.CoercionType.Text,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve({ selectedText: result.value as string });
          } else {
            reject(new Error(result.error.message));
          }
        },
      );
    });
  },
};

export const sharedTools: OfficeToolDefinition[] = [getDocumentInfoTool, getSelectionTool];
